<template>
  <v-container class="daily-view" ref="dailyviewcontainer" fill-height>
    <div><span class="title">入力チェック</span></div>

    <v-row dense>
      <v-col cols="12" sm="6" md="3" lg="3">
        <DateInputField v-model="selectedDate" label="検索日"></DateInputField>
      </v-col>
    </v-row>

    <v-row dense>
      <v-btn v-on:click="fetchData" class="primary mr-3">検索</v-btn>
      <v-btn v-on:click="onDownloadClick" :loading="downloading"
        >ダウンロード</v-btn
      >
    </v-row>

    <v-row ref="dailyviewtable" dense>
      <v-simple-table fixed-header :height="tableHeight" v-if="!loading">
        <!--  fixed-header height="300px" -->
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th v-for="dt in headerRecords" class="text-left">{{ dt }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="groupRec in reportGroups">
              <tr v-if="groupRec.staffs.length > 0">
                <td>【{{ groupRec.group.groupName }}】</td>
                <td colspan="10"></td>
              </tr>

              <tr v-for="staffRec in groupRec.staffs" :key="staffRec.staffCd">
                <td>
                  <span class="pl-5">{{ staffRec.name }}</span>
                </td>
                <td class="text-left" v-for="exists in staffRec.records">
                  <span v-if="exists"><v-icon>check</v-icon></span>
                  <span v-else></span>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
    </v-row>

    <v-container v-if="loading" fluid fill-height>
      <v-row align="center" justify="center">
        <v-col cols="2">
          <v-progress-circular
            v-if="loading"
            :size="50"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import moment from "moment";
import Common from "@/components/Common";
import EntryCheckAPI from "@/apis/EntryCheckAPI";
import EntryCheckExcel from "@/utils/EntryCheckExcel";
import { currentTenant } from "@/tenant";
import DateInputField from "../components/controls/DateInputField";

// 一覧に表示する日数（検索日を末尾とする直近10日間）
const LIST_DAYS = 10;

export default {
  mixins: [Common],
  components: {
    DateInputField,
  },
  mounted: function () {
    let m = moment();
    let h = m.hour();

    // 17時前は前日の入力状況を確認する
    if (h < 17) {
      m.subtract(1, "days");
    }

    this.selectedDate = m.format("YYYY-MM-DD");

    this.fetchData();

    this.checkHeight();

    window.addEventListener("resize", this.handleResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  data() {
    return {
      loading: false,
      downloading: false,

      selectedDate: "",

      headerRecords: [],

      reportGroups: [],

      containerHeight: 100,
    };
  },
  computed: {
    tableHeight() {
      return "" + this.containerHeight + "px";
    },
  },
  methods: {
    checkHeight() {
      let clientHeight = this.$refs.dailyviewcontainer.clientHeight;

      this.containerHeight = clientHeight - 200;
    },

    handleResize() {
      this.checkHeight();
    },

    fetchData() {
      let self = this;

      if (!this.selectedDate) {
        return;
      }

      this.loading = true;

      this.reportGroups.splice(0);

      let requestData = {
        toDate: moment(this.selectedDate).format("YYYY-MM-DD"),
        days: LIST_DAYS,
      };

      Promise.resolve()
        .then(() => {
          return this.checkAuthToken();
        })
        .then(() => {
          return EntryCheckAPI.getData(requestData);
        })
        .then((a) => {
          this.makeData(a);

          this.loading = false;
        })
        .catch(function (err) {
          self.loading = false;
          console.log(err);
          self.showErrorToast("データの取得に失敗しました。");
        });
    },

    makeData(responseData) {
      this.reportGroups.splice(0);
      this.headerRecords.splice(0);

      let groupRecords = responseData.groups || [];
      let entryRecords = responseData.entries || [];

      entryRecords.forEach((dateRec) => {
        this.headerRecords.push(this.formatDate(dateRec.date, "MM/DD"));
      });

      groupRecords.forEach((groupRecord) => {
        // 日付ごとに、日報のある担当者かどうかを求める
        groupRecord.staffs.forEach((staff) => {
          staff.records = entryRecords.map(
            (dateRec) =>
              !!dateRec.reports.find(
                (element) => element.staffCd === staff.staffCd
              )
          );
        });

        this.reportGroups.push(groupRecord);
      });
    },

    // 検索日の年月を締め月とする入力チェックリスト（Excel）をブラウザ上で作成して保存する（SPEC-037）
    onDownloadClick() {
      let self = this;

      if (!this.selectedDate) {
        return;
      }

      this.downloading = true;

      // 締め期間は会社ごとの締め日で決める（SPEC-039 BR-E1。mykk: 21日〜20日、daiwa: 暦月）
      let period = EntryCheckExcel.calcClosingPeriod(
        moment(this.selectedDate).format("YYYY-MM-DD"),
        currentTenant.closingDay
      );

      Promise.resolve()
        .then(() => {
          return this.checkAuthToken();
        })
        .then(() => {
          // 締め期間のデータと ExcelJS（ダウンロード時のみ読み込む）を並行して取得する
          return Promise.all([
            EntryCheckAPI.getData({ toDate: period.toDate, days: period.days }),
            import(
              /* webpackChunkName: "exceljs" */ "exceljs/dist/exceljs.min.js"
            ),
          ]);
        })
        .then(([data, excelModule]) => {
          const ExcelJS = excelModule.default || excelModule;
          const workbook = EntryCheckExcel.buildEntryCheckWorkbook(ExcelJS, data);
          return workbook.xlsx.writeBuffer();
        })
        .then((buffer) => {
          this.saveFile(buffer, EntryCheckExcel.makeFileName(new Date()));
          self.downloading = false;
        })
        .catch(function (err) {
          self.downloading = false;
          console.log(err);
          self.showErrorToast("データの取得に失敗しました。");
        });
    },

    // バイナリをファイルとして保存させる
    saveFile(buffer, fileName) {
      let blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      let url = window.URL.createObjectURL(blob);
      let anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      // クリック直後に解放するとダウンロードが中断されるブラウザがあるため、少し待ってから解放する
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
  },
};
</script>
<style lang="scss" scoped></style>
